import { parseValueToDay } from '@ant-design/pro-components'
import dayjs from 'dayjs'
import { z } from 'zod'

export type CalendarValue = {
  startOf: (unit: 'day') => CalendarValue
  endOf: (unit: 'day') => CalendarValue
  toDate: () => Date
}

export type CalendarInput = CalendarValue | string | null | undefined
export type DateRangeFormValue = [CalendarInput, CalendarInput] | null | undefined

export const dateTimeStringSearchSchema = z.string().refine((value) => dayjs(value).isValid())

export const creatorMemberSearchShape = {
  creatorMemberId: z.coerce.number().int().positive().optional().catch(undefined)
} as const

export const createdAtSearchShape = {
  createdFrom: dateTimeStringSearchSchema.optional().catch(undefined),
  createdTo: dateTimeStringSearchSchema.optional().catch(undefined)
} as const

export const createdAtSortSearchShape = {
  sortField: z.enum(['createdAt']).optional().catch('createdAt').default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).optional().catch('desc').default('desc')
} as const

export function toCalendarValue(value: CalendarInput): CalendarValue | undefined {
  if (!value) {
    return undefined
  }
  if (typeof value !== 'string') {
    return value
  }
  const parsed = parseValueToDay(value)
  return parsed && !Array.isArray(parsed) ? parsed : undefined
}

export function getDateRangeFormValue(createdFrom?: string, createdTo?: string): DateRangeFormValue {
  const from = toCalendarValue(createdFrom)
  const to = toCalendarValue(createdTo)
  return from || to ? [from, to] : undefined
}

export function getCreatedAtSearch(value: DateRangeFormValue) {
  const createdFrom = toCalendarValue(value?.[0])
  const createdTo = toCalendarValue(value?.[1])
  return {
    createdFrom: createdFrom ? dayjs(createdFrom.startOf('day').toDate()).toISOString() : undefined,
    createdTo: createdTo ? dayjs(createdTo.endOf('day').toDate()).toISOString() : undefined
  }
}

export function getCreatedAtSortOrder(order: 'ascend' | 'descend' | null | undefined) {
  return order === 'ascend' ? 'asc' : 'desc'
}

export function getTableSortOrder(
  order: 'asc' | 'desc' | undefined,
  isActive = true
): 'ascend' | 'descend' | null | undefined {
  if (!isActive) {
    return null
  }
  if (order === 'asc') {
    return 'ascend'
  }
  if (order === 'desc') {
    return 'descend'
  }
  return undefined
}

export function getPositiveIntegerIdSearchValue(value: string | number | null | undefined) {
  const normalizedValue = typeof value === 'number' ? String(value) : value?.trim()
  if (!normalizedValue || !/^\d+$/.test(normalizedValue)) {
    return undefined
  }

  const id = Number(normalizedValue)
  return Number.isSafeInteger(id) && id > 0 ? id : undefined
}

/** URL 查询允许单值和多值；空输入留给 schema 的 optional/default 处理。 */
export function toSearchArray(value: unknown) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  return Array.isArray(value) ? value : [value]
}
